/**
 * Live select-field options, read off the production Projects schema.
 *
 * WHY THIS EXISTS (2026-08-18). Every picklist in this app is a hardcoded constant
 * (`SENDER_TYPE`, `AUD_TYPE`, `REQ_TYPE`, …). So when the ops team adds or renames an option
 * in Airtable, the form doesn't show it until someone edits the constant and ships a release.
 * Asked directly how fast an Airtable change reaches the form, the honest answer for these
 * fields was "never".
 *
 * The worker's `/field-options` route reads the select choices straight off production
 * Projects (Meta API, schema only — no records) keyed by Airtable FIELD NAME. Adding a new
 * picklist upstream therefore needs no change here at all.
 *
 * DESIGN NOTES
 * - Fetched ONCE per page load and memoised at module scope. These lists change a few times a
 *   year; re-fetching per component would be pointless traffic.
 * - The hardcoded constant stays the fallback and is what renders until the fetch resolves, so
 *   there is never an empty dropdown and a dead worker degrades to today's behaviour.
 * - Deliberately NOT surfacing a visible error. Falling back here means showing the same list
 *   the app has always shown — unlike the initiative picker, where the fallback was months
 *   stale and silence was the bug. A console warning is the right weight for this one.
 * - Airtable is the authority on WHICH options exist, but not on order or labels: callers map
 *   the live names back into their own shape (see AUD_TYPE's `CODE = Label` parsing in
 *   SendScope), so a live list can't break a control that expects codes.
 */
import { useEffect, useState } from "react";
import { getFieldOptions } from "./api";

export type FieldOptionMap = Record<string, string[]>;

let cache: FieldOptionMap | null = null;
let inflight: Promise<FieldOptionMap> | null = null;

async function load(): Promise<FieldOptionMap> {
  if (cache) return cache;
  if (!inflight) {
    inflight = getFieldOptions()
      .then((r) => {
        if (!r || !r.ok || !r.fields) throw new Error(r && r.error ? r.error : "bad shape");
        cache = r.fields;
        return cache;
      })
      .catch((e) => {
        /* eslint-disable-next-line no-console */
        console.warn("[field-options] live picklists unavailable, using built-in lists:", e && e.message);
        inflight = null;   // allow a later mount to retry
        return {} as FieldOptionMap;
      });
  }
  return inflight;
}

/** All live option lists, keyed by Airtable field name. `{}` until loaded or on failure. */
export function useFieldOptions(): FieldOptionMap {
  const [fields, setFields] = useState<FieldOptionMap>(cache || {});
  useEffect(() => {
    let cancelled = false;
    load().then((f) => { if (!cancelled) setFields(f); });
    return () => { cancelled = true; };
  }, []);
  return fields;
}

/**
 * One field's options, with the caller's constant as fallback.
 * Returns `fallback` until the fetch resolves, if the field name isn't found, or if the live
 * list is empty — so a rename upstream degrades to the built-in list rather than to nothing.
 */
export function useLiveOptions(fieldName: string, fallback: string[]): string[] {
  const all = useFieldOptions();
  const live = all[fieldName];
  return Array.isArray(live) && live.length ? live : fallback;
}
